/**
 * Can a cue's voicing make sound right now — and if not, why not, and for how long already.
 *
 * This answers ONE question, for everybody who needs it: the schedule (which must stop the lecture
 * until the answer is yes), the row (which draws the answer as a mark), and the footer (which counts
 * them). Before this module the answer was inferred from the DOM —
 * `document.querySelector('audio[data-cue=…]')?.readyState >= 2` — which cannot tell "the element
 * has not been created yet" from "there is no such cue", and cannot say anything at all about a cue
 * whose row has not rendered. Both distinctions matter: the first is the difference between waiting
 * and giving up, the second is most of the folder.
 *
 * Module state rather than React state on purpose. It is written from media events and read from a
 * 25ms scheduler tick; a render cycle in that path is exactly the latency being engineered out.
 *
 * PREPARATION TIME IS RECORDED, not just the outcome. Machines differ enormously here, and a
 * translator who says "it is slow for me" needs a number to point at rather than an impression.
 */

/** readyState at which an element will play through without stopping to buffer. */
const HAVE_FUTURE_DATA = 3;

export type ClipState =
    /** Nobody has asked for it: outside the prefetch window and off-screen. */
    | "idle"
    /** Being fetched or decoded right now. */
    | "loading"
    /** Its element exists and can make sound this instant. */
    | "ready"
    /** It will never play. The schedule passes it over rather than waiting forever. */
    | "error"
    /** Being re-synthesised: whatever bytes we hold are known to be stale. */
    | "editing";

export interface ClipInfo {
    state: ClipState;
    /** When the current state began — how long this machine has been preparing it. */
    since: number;
    /** How long preparation took, once it succeeded. Null while it has not. */
    readyMs: number | null;
    /** Why it failed, for the row's tooltip. */
    detail?: string;
}

const IDLE: ClipInfo = { state: "idle", since: 0, readyMs: null };

const info = new Map<string, ClipInfo>();
const media = new Map<string, HTMLMediaElement>();
/** Every cue of the folder that has a voicing at all — the denominator of the footer's count. */
let known: string[] = [];
/** The cues whose audio should be prepared now, nearest the playhead first. */
let wanted = new Set<string>();
/** The one cue the lecture is currently stopped for, if any. */
let awaited: string | null = null;

type StateListener = (id: string, next: ClipInfo) => void;
const stateListeners = new Set<StateListener>();
const wantListeners = new Set<() => void>();
const awaitedListeners = new Set<(id: string | null) => void>();

export const subscribeClipState = (fn: StateListener): (() => void) => {
    stateListeners.add(fn);
    return () => {
        stateListeners.delete(fn);
    };
};

export const subscribeClips = (fn: () => void): (() => void) => {
    wantListeners.add(fn);
    fn();
    return () => {
        wantListeners.delete(fn);
    };
};

export const subscribeAwaited = (fn: (id: string | null) => void): (() => void) => {
    awaitedListeners.add(fn);
    fn(awaited);
    return () => {
        awaitedListeners.delete(fn);
    };
};

export const clipInfo = (id: string): ClipInfo => info.get(id) ?? IDLE;

export const clipState = (id: string): ClipState => clipInfo(id).state;

/**
 * Record a state, and only notify when it is genuinely new.
 *
 * Subscribers include every row in the folder; this is called from media events that fire in
 * bursts, so churning them would cost more than the work being reported on.
 */
export const publishClipState = (id: string, state: ClipState, detail?: string): void => {
    const prev = info.get(id) ?? IDLE;
    if (prev.state === state && prev.detail === detail) return;

    const now = Date.now();
    const next: ClipInfo = {
        state,
        since: now,
        // Keep a measured preparation time once we have one: a clip that goes ready → idle →
        // ready (the row scrolled out of the want window and back) should not forget how long
        // this machine took the first time.
        readyMs:
            state === "ready"
                ? prev.state === "loading"
                    ? now - prev.since
                    : prev.readyMs
                : state === "editing" || state === "error"
                  ? null
                  : prev.readyMs,
        detail,
    };
    info.set(id, next);
    stateListeners.forEach((fn) => fn(id, next));
};

/**
 * Hand over the element that actually plays this cue.
 *
 * Readiness is published from here rather than guessed, because the element is the only thing that
 * knows. `canplaythrough` may have fired before we attached — an element fed from a blob URL is
 * often ready within the same frame — so the current value is checked imperatively as well.
 */
export const registerClipMedia = (id: string, el: HTMLMediaElement | null): void => {
    if (!el) {
        media.delete(id);
        // Losing the element un-readies the cue, but an error or an in-flight re-synthesis is a
        // fact about the FILE and outlives any particular element.
        const state = clipState(id);
        if (state !== "error" && state !== "editing") publishClipState(id, "idle");
        return;
    }
    media.set(id, el);
    if (el.readyState >= HAVE_FUTURE_DATA) publishClipState(id, "ready");
};

export const clipMedia = (id: string): HTMLMediaElement | null => media.get(id) ?? null;

/** True when the cue can make sound this instant. */
export const clipIsReady = (id: string): boolean => clipState(id) === "ready";

/**
 * True when waiting for this cue can never pay off — it failed, or its bytes are being replaced.
 * The schedule treats these exactly like "ready": it moves on rather than stopping the lecture.
 */
export const clipIsHopeless = (id: string): boolean => {
    const state = clipState(id);
    return state === "error" || state === "editing";
};

/** Every cue in the folder that has a voicing. Sets the denominator for {@link clipSummary}. */
export const setKnownClips = (ids: string[]): void => {
    known = ids;
};

/**
 * The cues to prepare now. Replaces the window wholesale — it is recomputed every tick from the
 * playhead, and a cue that has dropped out of it is no longer wanted.
 */
export const requestClips = (ids: string[]): void => {
    if (ids.length === wanted.size && ids.every((id) => wanted.has(id))) return;
    wanted = new Set(ids);
    wantListeners.forEach((fn) => fn());
};

export const isClipWanted = (id: string): boolean => wanted.has(id);

export const setAwaitedClip = (id: string | null): void => {
    if (awaited === id) return;
    awaited = id;
    awaitedListeners.forEach((fn) => fn(awaited));
};

export const awaitedClip = (): string | null => awaited;

/** How the folder stands, for the footer: enough to quote in a bug report. */
export const clipSummary = (): { ready: number; total: number; slowestMs: number } => {
    let ready = 0;
    let slowestMs = 0;
    known.forEach((id) => {
        const one = clipInfo(id);
        if (one.state === "ready") ready += 1;
        if (one.readyMs && one.readyMs > slowestMs) slowestMs = one.readyMs;
    });
    return { ready, total: known.length, slowestMs };
};

/** A different folder or language is a different set of cues entirely. */
export const forgetClips = (): void => {
    info.clear();
    media.clear();
    known = [];
    wanted = new Set();
    awaited = null;
    wantListeners.forEach((fn) => fn());
    awaitedListeners.forEach((fn) => fn(null));
};
